const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const FREE_API = `${BACKEND_URL}/api/free`;

// Session lives in an httpOnly cookie set by the server, so every request must
// send credentials. `free_token` is only read for users who signed in before the
// cookie migration — the server swaps it for a cookie on the first session check.
export const clearSessionHints = () => {
  localStorage.removeItem('free_token');
  localStorage.removeItem('free_email');
  localStorage.removeItem('free_signed_in');
};

export async function freeFetch(path, options = {}) {
  const legacyToken = localStorage.getItem('free_token');
  const res = await fetch(`${FREE_API}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    clearSessionHints();
    window.location.href = '/free';
    throw new Error('Session expired');
  }
  const data = await res.json();
  if (!res.ok) {
    let detail = 'Request failed';
    if (typeof data.detail === 'string') {
      detail = data.detail;
    } else if (Array.isArray(data.detail) && data.detail[0]?.msg) {
      detail = String(data.detail[0].msg).replace(/^Value error,\s*/, '');
    }
    throw new Error(detail);
  }
  return data;
}

// Accepts a weight typed with an optional trailing "kg"/"kgs" suffix (any case).
// Strips whitespace and the suffix, then returns a Number. Rejects anything with
// stray characters in the middle (e.g. "4kg0") by returning NaN.
export function parseWeight(raw) {
  if (raw === null || raw === undefined) return NaN;
  const stripped = String(raw).trim().toLowerCase().replace(/\s*kgs?$/, '').trim();
  if (stripped === '' || !/^\d*\.?\d+$/.test(stripped)) return NaN;
  return parseFloat(stripped);
}

export const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Year-less form for the My Progress table at phone width — full date stays
// on the session detail page via formatDate above.
export const formatDateShort = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const inputClass =
  'w-full bg-mono-card border border-mono-line text-white rounded-none px-3 py-2.5 font-body free-input-text focus:outline-none focus:border-white transition-colors';

// Bumped whenever a fix needs to be visually confirmed on an installed
// device (e.g. iOS home-screen PWA can lag behind a fresh deploy).
export const BUILD_STAMP = 'build 2026.11.03-compliance';

// Grip is stored per set. Stored values never change; wording lives here only.
export const GRIPS = ['pullup', 'chinup', 'neutral'];

export const GRIP_LABELS = {
  pullup: 'Pull-up',
  chinup: 'Chin-up',
  neutral: 'Neutral',
};

// Per-grip tile headings on My Progress. Pull-up keeps its original wording.
export const GRIP_TILE_LABELS = {
  pullup: 'Highest weighted pull-up logged',
  chinup: 'Best chin-up',
  neutral: 'Best neutral grip',
};

export const GRIP_CHART_TITLES = {
  pullup: 'Pull-up Strength',
  chinup: 'Chin-up Strength',
  neutral: 'Neutral Grip Strength',
};

export const nextGrip = (grip) => GRIPS[(GRIPS.indexOf(grip) + 1) % GRIPS.length];

// Reps logged under one grip, in set order. Used wherever a mixed-grip session
// has to render as that grip only.
export const gripReps = (session, grip) =>
  [1, 2, 3]
    .filter((n) => Number(session[`set${n}_reps`]) > 0 && (session[`set${n}_grip`] || 'pullup') === grip)
    .map((n) => session[`set${n}_reps`]);

export const labelClass =
  'block text-mono-mute font-heading text-xs font-semibold uppercase tracking-[0.15em] mb-1.5';

// Canonical "how hard was it" copy — plain-English only, no RPE numbers shown
// anywhere. Stored value is always the number (6-10); these are display-only.
export const RPE_LABELS = {
  6: '4 reps left in the tank',
  7: '3 reps left in the tank',
  8: '2 reps left in the tank',
  9: '1 rep left in the tank',
  10: 'Nothing left — absolute limit',
};

export const RPE_SHORT_LABELS = {
  6: '4 reps left',
  7: '3 reps left',
  8: '2 reps left',
  9: '1 rep left',
  10: 'Nothing left',
};

// Stored session_type values ("heavy" / "volume" / "other") deliberately differ
// from what users see. Do not rename the stored value to match display copy —
// wording lives here ONLY so it can change again without touching the DB,
// the plateau grouping (which groups by the stored value), or any migration.
export const SESSION_TYPE_LABELS = {
  heavy: 'Low reps (1–5)',
  volume: 'Higher reps (6+)',
  other: 'Other',
};

// Adjective form for sentences (e.g. the plateau card: "3 low-rep sessions running").
export const SESSION_TYPE_PLATEAU_LABELS = {
  heavy: 'low-rep',
  volume: 'higher-rep',
};

// Table-only short form (no bracketed rep range) — used ONLY in the My Progress
// table. The dropdown and session detail page keep the full SESSION_TYPE_LABELS.
export const SESSION_TYPE_TABLE_LABELS = {
  heavy: 'Low reps',
  volume: 'Higher reps',
  other: 'Other',
};
